import { Box, Toolbar } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { getOrderStatus } from "@/utils/getOrderStatus";
import { DataTable } from "@/components/dashboard";
import {
    OrderStatus,
    OrderStatusChip,
} from "@/components/dashboard/orders/status/OrderStatusChip";
import { EnhancedTableToolBar } from "@/components/dashboard/EnhancedTableToolBar";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { formatDate } from "@/utils";
import { useNavigate } from "react-router-dom";
import { getCustomOrders, IDBCustomOrder } from "@/api/orders";
import { CustomUrlCard } from "@/pages/store/components/CustomUrlCard";
import { useAsync } from "@react-hookz/web";
import { useFilterModel } from "@/hooks";

interface IAdminOrderRow {
    id: number;
    created_at: Date;
    customer: string;
    item_description: string;
    driver: string;
    paid_driver: Omit<
        OrderStatus,
        "pending" | "accepted" | "picked_up" | "delivered"
    >;
    order_status: Omit<OrderStatus, "paid" | "unpaid">;
    order: IDBCustomOrder;
}

export const AdminCustomOrders = () => {
    const navigate = useNavigate();
    const createOrderUrl = `${window.location.origin}/orders/create`;
    const [getOrdersState, getOrdersActions] = useAsync(getCustomOrders);
    const [orders, setOrders] = useState<IAdminOrderRow[]>([]);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    // set default pagination model
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const autoSizeOptions = {
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    useEffect(() => {
        getOrdersActions.execute(
            paginationModel.page + 1,
            paginationModel.pageSize,
            filterModel
        );
    }, [
        getOrdersActions,
        paginationModel.page,
        paginationModel.pageSize,
        filterModel,
    ]);

    useEffect(() => {
        if (getOrdersState.status === "success" && getOrdersState.result) {
            const orderRows = getOrdersState.result.items.map((order) => {
                return {
                    id: order.id,
                    created_at: new Date(order.created_at),
                    customer: `${order.first_name} ${order.last_name}`,
                    item_description: order.item_description,
                    driver: order.driver
                        ? `${order.driver.first_name} ${order.driver.last_name}`
                        : "Unaccepted",
                    order: order,
                    paid_driver: order.paid_driver ? "paid" : "unpaid",
                    order_status: getOrderStatus(order),
                } as IAdminOrderRow;
            });
            setOrders(orderRows.flat());
        }
    }, [getOrdersState]);

    const handleRowClick = (params: GridRowParams) => {
        navigate(`${params.row.id}`);
    };

    const columns: GridColDef<IAdminOrderRow>[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "Order",
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => {
                    if (!value) return value;

                    return `#${value}`;
                },
                display: "flex",
            },
            {
                field: "created_at",
                headerName: "Date",
                display: "flex",
                valueFormatter: (value) => {
                    if (!value) return value;

                    return formatDate(value);
                },
            },
            { field: "customer", headerName: "Customer", display: "flex" },
            {
                field: "item_description",
                headerName: "Item Description",
                display: "flex",
            },
            {
                field: "driver",
                headerName: "Driver",
                display: "flex",
            },
            {
                field: "paid_driver",
                headerName: "Paid Driver",
                type: "singleSelect",
                valueOptions: [
                    { value: "paid", label: "Paid" },
                    { value: "unpaid", label: "Unpaid" },
                ],
                renderCell({ row }) {
                    return (
                        <OrderStatusChip
                            orderStatus={
                                row.order.paid_driver ? "paid" : "unpaid"
                            }
                            order_id={row.id}
                            allowStatusUpdate
                        />
                    );
                },
            },
            {
                field: "order_status",
                type: "singleSelect",
                headerName: "Order Status",
                valueOptions: [
                    { value: "pending", label: "Pending" },
                    { value: "accepted", label: "Accepted" },
                    { value: "picked_up", label: "Picked Up" },
                    { value: "delivered", label: "Delivered" },
                ],
                renderCell({ row }) {
                    return (
                        <OrderStatusChip
                            orderStatus={getOrderStatus(row.order)}
                            order_id={row.id}
                            allowStatusUpdate
                        />
                    );
                },
            },
        ],
        []
    );

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(getOrdersState.result?.total || 0);

    const rowCount = useMemo(() => {
        if (getOrdersState.result?.total !== undefined) {
            rowCountRef.current = getOrdersState.result?.total;
        }
        return rowCountRef.current;
    }, [getOrdersState.result?.total]);

    return (
        <Box
            sx={{
                backgroundColor: "#F4F7FE",
                minHeight: "100vh",
                overflowX: "auto",
                width: "100%",
            }}
        >
            {/* Spacing for mobile app bar */}
            <Toolbar sx={{ display: { sm: "none" } }} />
            <Box sx={{ padding: "1em" }}>
                <EnhancedTableToolBar title={"Admin Custom Orders"} />
                <Box display={"flex"} pb={4}>
                    <CustomUrlCard url={createOrderUrl} />
                </Box>
                <DataTable
                    rows={orders}
                    loading={getOrdersState.status === "loading"}
                    sx={{
                        ".MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        "& .MuiDataGrid-row:hover": {
                            cursor: "pointer",
                        },
                    }}
                    columns={columns}
                    onRowClick={handleRowClick}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: "id", sort: "desc" }],
                        },
                    }}
                    autoSize
                    autosizeOptions={autoSizeOptions}
                    paginationMode={"server"}
                    rowCount={rowCount}
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    filterMode={"server"}
                    sortingMode={"server"}
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                />
            </Box>
        </Box>
    );
};
